import { useMemo, useState } from "react";
import { ROUTES, SELF_ONBOARDING_ROUTES } from "src/consts/routes";
import { useNavigate } from "react-router-dom";
import Modal from "src/components/Modal/Modal";
import { useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import { useToggle } from "src/hooks/useToggle";
import { Stack, useTheme, Box, CardMedia } from "@mui/material";
import { Tariff } from "src/models/clientsModel";
import PageTitle from "src/components/Displays/PageTitle";
import FaqOverview from "./components/FaqOverview";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import {
  BONUS,
  SESSION_KEYS,
  UI_FLOW_PREFERENCES,
  USER_PROFILE_DATA,
} from "src/consts/session";
import WhatsAppBox from "../WhatsAppBox";
import TarifSelectionContainer from "src/pages/Tarif/components/TarifSelectionContainer";
import { Certificate1, Certificate2 } from "src/assets";
import LogoSMA from "src/assets/icons/logo_sma.svg";
import { LichtBlickLogoSMA, Logo, LichtBlickLogo } from "src/assets";
import { ThemeValues } from "../../components/ThemeUsage/theme";
import { ThemeContext } from "../../components/ThemeUsage/ThemeUsage";
import { useContext } from "react";
import { ArrowLeft } from "src/assets";
import { dateFormat } from "src/helpers/dateFormat";

const TarifSelection = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const themeName = useContext(ThemeContext);

  const [showTariffTimeline] = useState(true);
  const [isFaqOverlayOpen, toggleFaqIsOverlayOpen] = useToggle(false);

  const [getBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);
  const showBonusCampaignFooter = getBonus()?.isActive ?? false;

  const [getSessionStorageItem] = useSessionStorage<Tariff[]>(
    SESSION_KEYS.TARIFFS,
  );
  const [userProfileData] = useSessionStorage<USER_PROFILE_DATA>(
    SESSION_KEYS.USER_PROFILE_DATA,
  );
  const [getUiFlowPreferences] = useSessionStorage<UI_FLOW_PREFERENCES>(
    SESSION_KEYS.UI_FLOW_PREFERENCES,
  );

  const { tariffsData } = useMemo(() => {
    return { tariffsData: getSessionStorageItem() };
  }, [getSessionStorageItem]);

  const CERTIFICATES = [
    { img: Certificate1, alt: "Zertifikat1" },
    { img: Certificate2, alt: "Zertifikat2" },
  ];

  const savingsData = tariffsData && tariffsData[0];
  const monthlyCosts = savingsData?.fixTariff?.monthlyCosts ?? "";
  const yearlyCosts = savingsData?.fixTariff?.yearlyCosts ?? "";
  const yearlyGridSupplyKwh = userProfileData()?.yearlyGridSupplyKwh ?? "";
  const bonusEndDate = getBonus()?.endDate ?? "";

  const footnote1Text = t("page.tariff.footnotes.footnote1");
  const footnote2Text = t("page.tariff.footnotes.footnote2", {
    yearlyGridSupplyKwh,
    monthlyCosts,
    yearlyCosts,
  });
  const footnote3Text = t("page.tariff.footnotes.footnote3", {
    bonusEndDate: dateFormat(bonusEndDate),
  });
  const footnoteLastText = t("page.tariff.footnotes.last");

  const [getTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);
  const [getSalesPartnerId] = useSessionStorage<string>(
    SESSION_KEYS.SALES_PARTNER_ID,
  );

  const transferId = getTransferId();
  const salesPartnerId = getSalesPartnerId();

  const isCustomTheme = themeName === "sma" || themeName === "disco";

  const [, setTariffs] = useSessionStorage<Tariff[]>(SESSION_KEYS.TARIFFS);
  const [, setBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);

  const getLogo = () => {
    switch (themeName) {
      case ThemeValues.SMA:
      case ThemeValues.lichtblick:
        return <LichtBlickLogoSMA />;
      case ThemeValues.default:
      default:
        return <Logo color="white" />;
    }
  };

  const horizontalPadding =
    themeName === ThemeValues.SMA ? { xs: 6, sm: 10.5 } : { xs: 4, sm: 9 };

  if (!tariffsData || tariffsData.length === 0) {
    return (
      <PrimaryButton
        text={t("page.introduction.textNewCalculation")}
        onClick={() => navigate(ROUTES.INTRODUCTION)}
        sx={{
          textTransform: "none",
          background: isCustomTheme ? undefined : "white",
          color: isCustomTheme ? undefined : "black !important",
          "&:hover": {
            background: isCustomTheme ? undefined : "white",
            color: isCustomTheme ? undefined : "black",
          },
        }}
      />
    );
  }

  const navigateTo = () => {
    const uiFlowPreferences = getUiFlowPreferences();
    if (
      uiFlowPreferences &&
      uiFlowPreferences.skipOemAuth &&
      uiFlowPreferences.skipOemAuth === "false" &&
      uiFlowPreferences.processType === "SITE_FULL_SELF_V1"
    ) {
      return SELF_ONBOARDING_ROUTES.ACCESS;
    }
    return ROUTES.YOUR_NAME;
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        mt={2}
        px={horizontalPadding}
        mx={{ xs: -3, sm: -7.5 }}
        spacing={1}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            height: { xs: 30, sm: 50 },
            width: "auto",
          }}
        >
          {themeName === ThemeValues.SMA && (
            <Box
              component="img"
              src={LogoSMA}
              alt="Logo SMA"
              sx={{ height: "100%", width: "auto" }}
            />
          )}
          {themeName === ThemeValues.ison && <LichtBlickLogo color="white" />}
        </Stack>

        <Box
          sx={{
            height: { xs: 30, sm: 50 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {getLogo()}
        </Box>
      </Stack>

      <Stack gap={8}>
        <TarifSelectionContainer
          showTrustBadges={false}
          showSavingsResult={true}
          tariffsData={tariffsData}
          showTariffTimeline={showTariffTimeline}
          topTextKey="page.tariff.tariffSelectionContainer.howTariffChangeWorks"
        />
        <Stack gap={5}>
          <PrimaryButton
            text={t("page.calculatedSavingsSummary.choseContract")}
            onClick={() => navigate(navigateTo())}
          />

          <Stack
            mt={2}
            gap={5}
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 4 }}
          >
            {CERTIFICATES.map((cert, index) => (
              <CardMedia
                key={index}
                component="img"
                image={cert.img}
                alt={cert.alt}
                sx={{
                  height: "153px",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            ))}
          </Stack>

          <Stack
            sx={{ cursor: "pointer" }}
            direction="row"
            gap={1}
            justifyContent="center"
            onClick={() => {
              setBonus(null);
              setTariffs([]);
              navigate(ROUTES.PV_SETUP);
            }}
          >
            <ArrowLeft color={theme.palette.outlineButton.text} />
            <Text
              sx={(theme) => ({
                color: theme.palette.outlineButton.text,
              })}
              text={t("page.calculatedSavings.newPriceCalculation")}
            />
          </Stack>

          <Stack
            sx={{ cursor: "pointer" }}
            direction="row"
            gap={1}
            justifyContent="center"
            onClick={() =>
              navigate(
                ROUTES.INTRODUCTION +
                  `?tid=${transferId}&spid=${salesPartnerId}&theme=${themeName}&openSummary=true`,
              )
            }
          >
            <Text
              sx={(theme) => ({
                color: theme.palette.outlineButton.text,
              })}
              text={t("page.tariff.details")}
            />
          </Stack>

          <Stack
            gap={1}
            direction="row"
            justifyContent="center"
            sx={{ cursor: "pointer" }}
            onClick={toggleFaqIsOverlayOpen}
          >
            <Text
              sx={(theme) => ({
                color: theme.palette.outlineButton.text,
              })}
              text={t("page.tariff.faq")}
            />
          </Stack>
        </Stack>
        <WhatsAppBox />
        <Stack gap={4}>
          <Text
            sx={{
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            }}
            text={footnote1Text}
          />
          <Text
            sx={{
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            }}
            text={footnote2Text}
          />
          {showBonusCampaignFooter && (
            <Text
              sx={{
                color: "#848F9F",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "18px",
              }}
              text={footnote3Text}
            />
          )}
          <Text
            sx={{
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            }}
            text={footnoteLastText}
          />
        </Stack>
      </Stack>

      <Modal
        title={
          <PageTitle
            color={theme.palette.grey[500]}
            text={t("page.tariff.faqModal.title")}
          />
        }
        isOpen={isFaqOverlayOpen}
        onClose={toggleFaqIsOverlayOpen}
        onSubmit={toggleFaqIsOverlayOpen}
      >
        <FaqOverview />
      </Modal>
    </>
  );
};

export default TarifSelection;
